import type { Dispatch, KeyboardEvent as ReactKeyboardEvent, RefObject, SetStateAction } from 'react'
import { useEffect, useLayoutEffect, useReducer, useRef } from 'react'
import {
  getCellCaretAfterEdit,
  getCellDisplay,
  getEditedCellValue,
  isDeleteOfLeadingSign,
} from '../HourlyDataTable.cellEdit.helper'
import type {
  CellCaretPlacement,
  RowValues,
  TableCellAdapter,
  TableCellEditRule,
  TextSelection,
} from '../HourlyDataTable.types'

const INPUT_TYPES_EDITING_THE_SELECTION = /^(insertText|insertFromPaste|delete)/

type CellField = HTMLInputElement | HTMLTextAreaElement

type UseTableCellEditOptions = {
  adapter: TableCellAdapter
  inputRefs: RefObject<Record<string, CellField | null>>
  valuesByRow: RowValues[]
  setValuesByRow: Dispatch<SetStateAction<RowValues[]>>
  getCellRule: (cellKey: string) => TableCellEditRule
  getEditTargets: (cellKey: string) => ReadonlySet<string>
  takeReplaceOnType: (cellKey: string) => boolean
  handleNavigationKeyDown: (cellKey: string, event: ReactKeyboardEvent) => void
}

type UseTableCellEditResult = {
  handleCellValueChange: (cellKey: string, editedText: string) => void
  handleCellKeyDown: (cellKey: string, event: ReactKeyboardEvent) => void
}

type SelectionBeforeInput = TextSelection & {
  field: CellField
}

type CaretForCommit = {
  cellKey: string
  placement: CellCaretPlacement
}

function writeToEditTargets(
  valuesByRow: RowValues[],
  adapter: TableCellAdapter,
  editTargets: ReadonlySet<string>,
  value: string
): RowValues[] {
  const next = [...valuesByRow]
  editTargets.forEach((cellKey) => {
    const coordinate = adapter.getCoordinate(cellKey)
    if (coordinate) {
      next[coordinate.rowIndex] = { ...next[coordinate.rowIndex], [cellKey]: value }
    }
  })
  return next
}

function getCaretAtEnd(text: string): CellCaretPlacement {
  return { text, start: text.length, end: text.length }
}

function useSelectionBeforeInputRef(
  handleInputWithoutValueChange: (field: CellField) => void
): RefObject<SelectionBeforeInput | null> {
  const selectionBeforeInputRef = useRef<SelectionBeforeInput | null>(null)
  const handleInputWithoutValueChangeRef = useRef(handleInputWithoutValueChange)
  useLayoutEffect(() => {
    handleInputWithoutValueChangeRef.current = handleInputWithoutValueChange
  })

  useEffect(() => {
    function recordSelectionBeforeInput(event: InputEvent): void {
      const field = event.target
      selectionBeforeInputRef.current =
        (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) &&
        INPUT_TYPES_EDITING_THE_SELECTION.test(event.inputType) &&
        field.selectionStart !== null &&
        field.selectionEnd !== null
          ? { field, start: field.selectionStart, end: field.selectionEnd }
          : null
    }

    function settleInput(event: Event): void {
      const untakenSelection = selectionBeforeInputRef.current
      if (untakenSelection?.field === event.target) {
        handleInputWithoutValueChangeRef.current(untakenSelection.field)
      }
      selectionBeforeInputRef.current = null
    }

    document.addEventListener('beforeinput', recordSelectionBeforeInput, true)
    document.addEventListener('input', settleInput)
    return () => {
      document.removeEventListener('beforeinput', recordSelectionBeforeInput, true)
      document.removeEventListener('input', settleInput)
    }
  }, [])

  return selectionBeforeInputRef
}

function takeSelectionBeforeInput(
  selectionBeforeInputRef: RefObject<SelectionBeforeInput | null>,
  field: CellField | null | undefined
): TextSelection | undefined {
  const selectionBeforeInput = selectionBeforeInputRef.current
  selectionBeforeInputRef.current = null
  return selectionBeforeInput && selectionBeforeInput.field === field
    ? { start: selectionBeforeInput.start, end: selectionBeforeInput.end }
    : undefined
}

function useCaretForNextCommitRef(
  inputRefs: RefObject<Record<string, CellField | null>>
): RefObject<CaretForCommit | null> {
  const caretForNextCommitRef = useRef<CaretForCommit | null>(null)

  useLayoutEffect(() => {
    const caretForCommit = caretForNextCommitRef.current
    caretForNextCommitRef.current = null
    const field = caretForCommit && inputRefs.current[caretForCommit.cellKey]
    if (!caretForCommit || !field) {
      return
    }

    if (field.value !== caretForCommit.placement.text) {
      field.value = caretForCommit.placement.text
    }
    field.setSelectionRange(caretForCommit.placement.start, caretForCommit.placement.end)
  })

  return caretForNextCommitRef
}

export function useTableCellEdit(options: UseTableCellEditOptions): UseTableCellEditResult {
  const optionsRef = useRef(options)
  useLayoutEffect(() => {
    optionsRef.current = options
  })
  const selectionBeforeInputRef = useSelectionBeforeInputRef(handleInputWithoutValueChange)
  const caretForNextCommitRef = useCaretForNextCommitRef(options.inputRefs)
  const [, commitCaretPlacement] = useReducer((commitCount: number) => commitCount + 1, 0)

  function placeCaretOnNextCommit(cellKey: string, placement: CellCaretPlacement | null): void {
    if (placement) {
      caretForNextCommitRef.current = { cellKey, placement }
      commitCaretPlacement()
    }
  }

  function writeToCellEditTargets(cellKey: string, value: string): void {
    const { adapter, setValuesByRow, getEditTargets } = optionsRef.current
    const editTargets = getEditTargets(cellKey)
    setValuesByRow((previous) => writeToEditTargets(previous, adapter, editTargets, value))
  }

  function handleCellValueChange(cellKey: string, editedText: string): void {
    const { adapter, inputRefs, valuesByRow, getCellRule, takeReplaceOnType } = optionsRef.current
    const field = inputRefs.current[cellKey]
    const selectionBeforeEdit = takeSelectionBeforeInput(selectionBeforeInputRef, field)
    const coordinate = adapter.getCoordinate(cellKey)
    if (!coordinate) {
      return
    }

    const rule = getCellRule(cellKey)
    const storedValue = valuesByRow[coordinate.rowIndex]?.[cellKey] ?? ''
    const outcome = getEditedCellValue({ rule, storedValue, editedText, selectionBeforeEdit })
    const caret = field?.selectionStart
    const getCaretPlacement = (nextStoredValue: string): CellCaretPlacement | null =>
      typeof caret === 'number' ? getCellCaretAfterEdit({ rule, editedText, caret, nextStoredValue }) : null

    if (outcome.kind === 'refused') {
      placeCaretOnNextCommit(cellKey, outcome.restore)
      return
    }
    if (takeReplaceOnType(cellKey)) {
      placeCaretOnNextCommit(cellKey, getCaretAtEnd(getCellDisplay(outcome.insertedText, rule)))
      writeToCellEditTargets(cellKey, outcome.insertedText)
      return
    }
    if (outcome.kind === 'refusedUnlessReplacing') {
      placeCaretOnNextCommit(cellKey, outcome.restore)
      return
    }
    if (outcome.kind === 'unchanged') {
      placeCaretOnNextCommit(cellKey, getCaretPlacement(storedValue))
      return
    }

    placeCaretOnNextCommit(cellKey, getCaretPlacement(outcome.value))
    writeToCellEditTargets(cellKey, outcome.value)
  }

  function handleInputWithoutValueChange(field: CellField): void {
    const { inputRefs } = optionsRef.current
    const cellKey = Object.keys(inputRefs.current).find((key) => inputRefs.current[key] === field)
    if (cellKey !== undefined) {
      handleCellValueChange(cellKey, field.value)
    }
  }

  function handleCellKeyDown(cellKey: string, event: ReactKeyboardEvent): void {
    const { inputRefs, getCellRule, getEditTargets, handleNavigationKeyDown } = optionsRef.current
    const field = inputRefs.current[cellKey]
    if (
      field &&
      getEditTargets(cellKey).size === 1 &&
      isDeleteOfLeadingSign(getCellRule(cellKey), event.nativeEvent, field)
    ) {
      return
    }

    handleNavigationKeyDown(cellKey, event)
  }

  return { handleCellValueChange, handleCellKeyDown }
}
